import React, { useState } from 'react';
import AppHeader from '../../../components/AppHeader.tsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faChevronLeft, faChevronRight, faClock, faDownload, faEdit, faFilter, faPlus, faUser } from '@fortawesome/free-solid-svg-icons';

const StaffSchedulePage: React.FC = () => {
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [selectedStaff, setSelectedStaff] = useState('all');

  const breadcrumb = [{ label: 'Zaměstnanci' }, { label: 'Rozvrh zaměstnanců' }];

  const staff = [
    { id: '1', name: 'Anna Svobodová', role: 'Kadeřnice' },
    { id: '2', name: 'Petr Novák', role: 'Kadeřník' },
    { id: '3', name: 'Marie Krásná', role: 'Kadeřnice' },
    { id: '4', name: 'Jana Nová', role: 'Kosmetička' },
  ];

  const days = ['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne'];

  const getWeekDates = (date: Date) => {
    const week = [];
    const start = new Date(date);
    start.setDate(date.getDate() - date.getDay() + 1); // Start from Monday

    for (let i = 0; i < 7; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      week.push(day);
    }
    return week;
  };

  const weekDates = getWeekDates(currentWeek);

  const navigateWeek = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentWeek);
    newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
    setCurrentWeek(newDate);
  };

  const formatWeekRange = () => {
    const start = weekDates[0];
    const end = weekDates[6];
    return `${start.toLocaleDateString('cs-CZ')} - ${end.toLocaleDateString('cs-CZ')}`;
  };

  return (
    <div className="h-full flex flex-col">
      <AppHeader title="Rozvrh zaměstnanců" breadcrumb={breadcrumb} />

      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="p-6 space-y-6">
          {/* Header */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-2xl font-semibold text-gray-900">Rozvrh zaměstnanců</h1>
              <div className="flex items-center space-x-3">
                <button className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
                  <FontAwesomeIcon icon={faPlus} className="mr-2" />
                  Nový rozvrh
                </button>
                <button className="border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors">
                  <FontAwesomeIcon icon={faDownload} className="mr-2" />
                  Export
                </button>
              </div>
            </div>

            {/* Week Navigation */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <button onClick={() => navigateWeek('prev')} className="p-2 hover:bg-gray-100 rounded-lg">
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </button>
                  <h2 className="text-lg font-medium">{formatWeekRange()}</h2>
                  <button onClick={() => navigateWeek('next')} className="p-2 hover:bg-gray-100 rounded-lg">
                    <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                </div>
                <button onClick={() => setCurrentWeek(new Date())} className="text-sm text-green-600 hover:text-green-800">
                  Tento týden
                </button>
              </div>

              <div className="flex items-center space-x-3">
                <select value={selectedStaff} onChange={e => setSelectedStaff(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  <option value="all">Všichni zaměstnanci</option>
                  {staff.map(member => (
                    <option key={member.id} value={member.id}>
                      {member.name}
                    </option>
                  ))}
                </select>
                <button className="flex items-center px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                  <FontAwesomeIcon icon={faFilter} className="w-4 h-4 mr-2" />
                  Filtry
                </button>
              </div>
            </div>
          </section>

          {/* Schedule Grid */}
          <section className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48">Zaměstnanec</th>
                    {days.map((day, index) => (
                      <th key={index} className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div>{day}</div>
                        <div className="text-sm font-normal text-gray-900">{weekDates[index].getDate()}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {staff.map(member => (
                    <tr key={member.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                            <FontAwesomeIcon icon={faUser} className="w-5 h-5 text-green-600" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{member.name}</div>
                            <div className="text-sm text-gray-500">{member.role}</div>
                          </div>
                        </div>
                      </td>
                      {days.map((_, dayIndex) => (
                        <td key={dayIndex} className="px-6 py-4 text-center">
                          <div className="space-y-1">
                            <div className="text-xs text-gray-600">08:00-17:00</div>
                            <div className="flex flex-wrap gap-1 justify-center">
                              <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded">Práce</span>
                              <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded">12:00-13:00</span>
                            </div>
                            <button className="text-green-600 hover:text-green-800 text-xs">
                              <FontAwesomeIcon icon={faEdit} className="w-3 h-3" />
                            </button>
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Quick Actions */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Rychlé akce</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors">
                <FontAwesomeIcon icon={faPlus} className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-600">Přidat dovolenou</span>
              </button>
              <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-red-500 hover:bg-red-50 transition-colors">
                <FontAwesomeIcon icon={faClock} className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-600">Nemoc</span>
              </button>
              <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors">
                <FontAwesomeIcon icon={faCalendarAlt} className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-600">Hromadné úpravy</span>
              </button>
              <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors">
                <FontAwesomeIcon icon={faDownload} className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-600">Export rozvrhu</span>
              </button>
            </div>
          </section>

          {/* Legend */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Legenda</h3>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center">
                <span className="w-4 h-4 bg-green-100 rounded mr-2"></span>
                <span className="text-sm text-gray-600">Pracovní doba</span>
              </div>
              <div className="flex items-center">
                <span className="w-4 h-4 bg-yellow-100 rounded mr-2"></span>
                <span className="text-sm text-gray-600">Pauza</span>
              </div>
              <div className="flex items-center">
                <span className="w-4 h-4 bg-blue-100 rounded mr-2"></span>
                <span className="text-sm text-gray-600">Dovolená</span>
              </div>
              <div className="flex items-center">
                <span className="w-4 h-4 bg-red-100 rounded mr-2"></span>
                <span className="text-sm text-gray-600">Nemoc</span>
              </div>
              <div className="flex items-center">
                <span className="w-4 h-4 bg-gray-100 rounded mr-2"></span>
                <span className="text-sm text-gray-600">Mimo pracovní dobu</span>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default StaffSchedulePage;
